<template>
	<div class="product-attributes">
		<div class="form-group mb-3">
			<label
				><b>Attributes</b
				><i
					class="material-icons info"
					v-tooltip="'Ketik untuk menambah attribut lain'"
					>info</i
				></label
			>
			<multiselect
				placeholder="Input or Select Attributes"
				label="name"
				track-by="name"
				:options="defaultAttributes"
				:hide-selected="true"
				:multiple="true"
				:taggable="true"
				:reset-after="true"
				:show-labels="false"
				tag-placeholder="Press enter to insert Attribute"
				@tag="add"
				@select="select"
			>
			</multiselect>
		</div>
		<div class="attributes" v-if="attributes.length > 0">
			<product-attribute
				v-for="(attribute, index) in attributes"
				:key="index"
				:attribute="attribute"
				:index="index"
				:is-edit-product="isEditProduct"
				@remove="remove"
			>
			</product-attribute>
		</div>
		<div class="form-group mb-0">
			<label><b>Variations Price</b></label>
			<div class="attributes" v-if="prices.length > 0">
				<product-attribute-price
					v-for="(price, index) in prices"
					v-bind.sync="prices[index]"
					:key="index"
					:index="index"
					:wholesale-error="isPriceWholesaleError(index)"
					:commission-percentage="commissionPercentage"
					:outsource-commission="outsourceCommission"
					@show-wholesale="showWholesale(index)"
					@apply-all="applyAll"
					@change="onAttributePriceChange(index)"
					:weight-error="weightVariableError[index]"
					:enabled-error="enabledVariableError"
				>
				</product-attribute-price>
			</div>
		</div>
		<div class="selected-options-empty error" v-if="prices.length === 0">
			No Variations Available
		</div>
		<!-- Wholesale -->
		<modal
			size="medium"
			@close="isShowWholesale = false"
			v-if="isShowWholesale"
		>
			<div slot="header"><h3>Wholesale</h3></div>
			<div slot="body">
				<wholesale
					:items="prices[selectedPrice].wholesale"
					:price="prices[selectedPrice].price"
					@change="changeWholesale"
				>
				</wholesale>
			</div>
		</modal>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import { defaultAttributes, defaultStock, defaultMargin, defaultUom } from '../lib/statics';
import { createCombinations, duplicateVar } from '../lib/helper';
import Modal from './Modal.vue';
import Wholesale from './Wholesale.vue';
import ProductAttribute from './ProductAttribute.vue';
import ProductAttributePrice from './ProductAttributePrice.vue';

export default {
	name: 'productAttributes',
	props: {
		attributes: {
			type: Array,
			default: () => [],
		},
		prices: {
			type: Array,
			default: () => [],
		},
		defaultWeight: {
			type: Number,
			default: 0,
		},
		commissionPercentage: {
			type: Number,
			default: 5,
		},
		outsourceCommission: {
			type: Object,
			default: null,
		},
		isEditProduct: {
			type: Boolean,
			default: false,
		},
		weightVariableError: {
			type: Array,
			default: () => [],
		},
		enabledVariableError: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		ProductAttribute,
		ProductAttributePrice,
		Modal,
		Wholesale,
	},
	data() {
		return {
			defaultAttributes,
			separatorToken: '||',
			oldPrices: [],
			selectedPrice: null,
			isShowWholesale: false,
		};
	},
	methods: {
		...mapActions({
			changeVariableWholesaleError:
				'productCreator/changeVariableWholesaleError',
		}),
		validateMaxVariations() {
			let isMaximum = false;
			if (this.attributes.length >= 2) {
				isMaximum = true;
				this.$notify({
					group: 'app',
					type: 'warn',
					title: 'Maximum Variations',
					text: 'Variasi hanya boleh maksimal 2 attributes.',
				});
			}
			return isMaximum;
		},
		select(attribute) {
			if (this.validateMaxVariations()) {
				return;
			}
			const newAttribute = duplicateVar(attribute);
			this.attributes.push(newAttribute);
		},
		add(attrName) {
			if (this.validateMaxVariations()) {
				return;
			}
			const name = attrName.trim().replace(/[^\w\s]/gi, '');
			const attribute = {
				name,
				values: [],
				object_values: [],
			};
			this.attributes.push(attribute);
		},
		remove(index) {
			this.attributes.splice(index, 1);
		},
		generatePrices() {
			const separator = this.separatorToken;
			const defaultWeight = duplicateVar(this.defaultWeight);
			const variationsPricesMapper = (combination) => {
				const isArray = Array.isArray(combination);

				const tempName = this.getFixedAttribute(isArray, combination, 'text');
				const tempId = this.getFixedAttribute(isArray, combination, 'id');

				const name = isArray ? tempName.join(separator) : tempName;
				const id = isArray ? tempId.join(separator) : tempId;
				const stock = duplicateVar(defaultStock);
				const uom = duplicateVar(defaultUom);
				const price = {
					id,
					variant_code: '',
					name,
					price: 0,
					weight: defaultWeight,
					cogs: 0,
					stock,
					uom,
					margin: defaultMargin,
					wholesale: [],
					enabled: true,
				};
				return price;
			};
			const prices = this.combinations.map(variationsPricesMapper);

			// Get previous prices and weights from old prices
			this.prices.forEach((oldPrice) => {
				const hasIdProperty = Object.prototype.hasOwnProperty.call(
					oldPrice,
					'id',
				);

				const property = hasIdProperty ? 'id' : 'name';

				const price = prices.find(
					currPrice => currPrice[property] === oldPrice[property],
				);
				if (price) {
					price.price = oldPrice.price;
					price.weight = oldPrice.weight;
					price.cogs = oldPrice.cogs;
					if (oldPrice.wholesale) {
						price.wholesale = oldPrice.wholesale;
					}
					if (oldPrice.stock) {
						price.stock = oldPrice.stock;
					}
					if (oldPrice.variant_code) {
						price.variant_code = oldPrice.variant_code;
					}
					if (oldPrice.margin) {
						price.margin = oldPrice.margin;
					}
					if (typeof oldPrice.enabled !== 'undefined') {
						price.enabled = oldPrice.enabled;
					}
				}
			});
			this.$emit('change', prices);
		},
		changeWholesale(items) {
			this.prices[this.selectedPrice].wholesale = items;
			this.checkWholesaleError();
		},
		showWholesale(index) {
			this.selectedPrice = index;
			this.isShowWholesale = true;
		},
		addWholesale() {
			this.prices.forEach((price) => {
				if (!price.wholesale) {
					// eslint-disable-next-line no-param-reassign
					price.wholesale = [];
				}
			});
		},
		addEnabled() {
			this.prices.forEach((price) => {
				if (typeof price.enabled === 'undefined') {
					// eslint-disable-next-line no-param-reassign
					price.enabled = true;
				}
			});
		},
		isPriceWholesaleError(i) {
			let isError = false;
			const priceItem = this.prices[i];
			const initialPrice = priceItem.price;
			if (priceItem.wholesale) {
				const wholesales = priceItem.wholesale;
				for (let index = 0; index < wholesales.length; index++) {
					const isDisabled = this.isFieldDisabled(wholesales, index);
					const isMinError = this.isFieldError(
						wholesales,
						index,
						'min',
						initialPrice,
					);
					const isPriceError = this.isFieldError(
						wholesales,
						index,
						'price',
						initialPrice,
					);
					isError = !isDisabled && (isMinError || isPriceError);
					if (isError) {
						break;
					}
				}
			}
			return isError;
		},
		checkWholesaleError() {
			let isError = false;
			for (let i = 0; i < this.prices.length; i++) {
				isError = this.isPriceWholesaleError(i);
				if (isError) {
					break;
				}
			}
			this.changeVariableWholesaleError(isError);
		},
		isFieldDisabled(items, index) {
			let isInvalid = false;
			const prevIndex = index - 1;
			const prevItem = items[prevIndex];
			if (prevItem) {
				const isPrevPriceEmpty = prevItem.price === '';
				const isPrevMinEmpty = prevItem.min === '';
				const isPrevPriceError = this.isFieldError(items, prevIndex, 'price');
				const isPrevMinError = this.isFieldError(items, prevIndex, 'min');
				const isPrevDisabled = this.isFieldDisabled(items, prevIndex);
				isInvalid =					isPrevPriceEmpty
					|| isPrevMinEmpty
					|| isPrevPriceError
					|| isPrevMinError
					|| isPrevDisabled;
			}
			return isInvalid;
		},
		getFieldError(items, index, field, initialPrice) {
			let errorMessage = '';
			const prevIndex = index - 1;
			const prevItem = items[prevIndex];
			const item = items[index];
			if (item) {
				if (field === 'min') {
					const isMinError = item.price !== '' && item.min === '';
					const isMinNotEmpty = item.min !== '';
					const isValueNotValid =						isMinNotEmpty
						&& (item.min === 1 || (prevItem && item.min <= prevItem.min));
					const isError = isMinError || isValueNotValid;
					if (isError) {
						errorMessage = 'Minimum quantity is not valid.';
					}
				} else if (field === 'price') {
					const isPriceHigher = prevItem && item.price >= prevItem.price;
					const isFirstPriceHigher = index === 0 && item.price >= initialPrice;
					const isPriceEmtpy = item.min !== '' && item.price === '';
					if (isPriceEmtpy || item.price === 0) {
						errorMessage = 'Price is not valid.';
					} else if (isPriceHigher) {
						errorMessage = 'Price must be cheaper.';
					} else if (isFirstPriceHigher) {
						errorMessage = 'Must be cheaper than normal price.';
					}
				}
			}
			return errorMessage;
		},
		isFieldError(items, index, field, initialPrice) {
			const error = this.getFieldError(items, index, field, initialPrice);
			return error.length > 0;
		},
		onAttributePriceChange() {
			this.checkWholesaleError();
			this.$emit('weight-changed', this.prices);
			this.$emit('enabled-changed', this.prices);
		},
		applyAll(params) {
			const prices = this.prices.map((price) => {
				const newPrice = price;
				newPrice[params.type] = params.value;
				return newPrice;
			});
			this.$emit('change', prices);
			const notifyMessage = `Product Variations ${params.type} updated`;
			this.$notify({
				group: 'productCreator',
				text: notifyMessage,
				type: 'success',
			});
		},
		isObject(param) {
			return typeof param === 'object' && param !== null;
		},
		getFixedAttribute(isArray, combination, attribute) {
			let result = '';
			if (isArray) {
				result = combination;
				if (this.isObject(combination[0])) {
					result = combination.map(variation => variation[attribute]);
				}
			} else {
				result = combination;
				if (this.isObject(combination)) {
					result = combination[attribute];
				}
			}
			return result;
		},
	},
	computed: {
		combinations() {
			const attributesOptions = this.attributes.map(
				attribute => attribute.object_values,
			);
			const hasAttributes = attributesOptions.length > 0;
			const combinations = hasAttributes
				? createCombinations(attributesOptions)
				: [];
			return combinations;
		},
	},
	watch: {
		combinations() {
			this.generatePrices();
		},
	},
	created() {
		this.addWholesale();
		this.addEnabled();
		this.checkWholesaleError();
	},
};
</script>

<style lang="scss">
@import '../assets/scss/modules/_variables.scss';

.attributes {
	margin-bottom: 1em;
	background-color: $color__blue-sky;
	border-radius: 3px;
	border: 1px solid $color__border;

	.form-control {
		background-color: #fff;
	}
}
</style>
